import { useMutation } from '@tanstack/react-query';
import axios from 'axios';

import { tpoGenUrls } from 'config/urls';
import { readAccessToken } from '../../utils/readAccessToken';
import type { CreateReportExportRequest, CreateReportExportResponse, ExportScope } from '../../types';

/**
 * `scope` rides along as a mutation variable so `onSuccess` can stamp it on the
 * job record for analytics, then is stripped before the POST — the backend has
 * no `scope` field. Keeping it here rather than passing per-call `onSuccess`
 * options means the `addJob` side effect lives in one place instead of being
 * repeated in all four views.
 */
export interface CreateReportExportVariables extends CreateReportExportRequest {
    /** Frontend-only: recorded on the job for analytics, never sent to the backend. */
    scope: ExportScope;
}

const createReportExport = async (
    planId: string,
    { scope, ...body }: CreateReportExportVariables,
): Promise<CreateReportExportResponse> => {
    const response = await axios(tpoGenUrls.createReportExport(planId), {
        method: 'POST',
        headers: { Authorization: `Bearer ${readAccessToken()}` },
        data: body,
    });
    return response.data;
};

interface UseCreateReportExportOptions {
    planId: string;
    onSuccess?: (data: CreateReportExportResponse, variables: CreateReportExportVariables) => void;
    onError?: (message: string) => void;
}

const useCreateReportExportMutation = ({ planId, onSuccess, onError }: UseCreateReportExportOptions) => {
    const { mutate, isPending } = useMutation({
        mutationFn: (variables: CreateReportExportVariables) => createReportExport(planId, variables),
        onSuccess,
        onError: (error: unknown) => {
            const err = error as {
                response?: { data?: { detail?: unknown; message?: string } };
                message?: string;
            };
            const detail = err?.response?.data?.detail;
            const resolved = typeof detail === 'string' ? detail : err?.response?.data?.message ?? err?.message ?? '';
            onError?.(resolved);
        },
    });

    return { createExport: mutate, isCreating: isPending };
};

export default useCreateReportExportMutation;
